import { useState, useRef, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Tooltip } from "@mui/material";
import { Grid, Container } from "@mui/material";
import Template from "../../../components/Template";
import Button from "../../../components/Button";
import SimpleModal from "../../../components/Modal";
import RoundButton from "../../../components/RoundButton";
import Fab from "../../../components/Fab";
import FilterSelect from "../../../components/FilterSelect";
import { colors } from "../../../constants/colors";
import _ from "lodash";
import Snackbar from "../../../components/Snackbar";
import { commonStrings } from "../../../components/StringBundle";
import { useGlobalStyles } from "../../../components/globalStyles";
import { convertToInt, company_name_list, isCompanyRestrictedRole, getCompanyFilterList, getDefaultCompany} from "../../common";
import useToken from "../../../hooks/useToken";
import SoftSchedulingList from "./SoftSchedulingList";
import roles from "../../../constants/roles";
import { getPlantCapacity } from "../../../apis";
import { status_list } from "./constant";
import { factoryCodesArr } from "./constant";
import { softScheduleStrings } from "../../../components/StringBundle";
import { hideExportExcel } from "../../../utils/utility";

const useStyles = makeStyles((theme) => ({
  modal: {
    position: "absolute",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    margin: "auto",
    top: "50%",
    right: "50%",
    transform: "translate(50%, -50%)",
    width: 500,
    backgroundColor: theme.palette.background.paper,
    border: "1px solid #fefefe",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  // ── Soft Schedule Grid page header — scoped to this page only ──────
  // Single-line header: filters + plant buttons left-aligned with equal
  // gaps, Export button pinned to the far right. Falls back to wrapping
  // only below the `sm` breakpoint so nothing is ever clipped on mobile.
  headerRow: {
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    margin: "0px 10px",
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
      rowGap: theme.spacing(1.5),
    },
    marginBottom:"10px"
  },
  headerLeft: {
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flex: "1 1 auto",
    minWidth: 0,
    // Safety net only — never shows on normal desktop widths, just
    // prevents controls from being clipped on very narrow viewports.
    overflowX: "auto",
    overflowY: "hidden",
    paddingTop: 8,
    paddingBottom: 2,
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
      overflowX: "visible",
    },
  },
  filterItem: {
    flex: "0 0 auto",
    minWidth: 150,
  },
  // Wraps each plant RoundButton so we can compact its default size
  // (150px min-width, 8px theme margin) via the `data-testid` it
  // already renders, without touching the shared RoundButton component.
  plantButtonWrap: {
    flex: "0 0 auto",
    display: "inline-flex",
    "& [data-testid='round-button']": {
      margin: 0,
      minWidth: 110,
      height: 40,
      padding: "0 16px",
      fontSize: 13,
      boxShadow: "none",
    },
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    flex: "0 0 auto",
  },
}));

const SoftScheduling = () => {
  const { getCurrentUserDetails } = useToken();
  const currentUserDetails = getCurrentUserDetails();
  const currentUserRole = currentUserDetails?.role;
  const classes = useStyles();
  const globalClasses = useGlobalStyles();
  const exportExcelRef = useRef();

  const savedPageNumber = sessionStorage.getItem("selectedpagenumber");
  const initialPageNumber = savedPageNumber ? convertToInt(savedPageNumber) : 1;
  const [activepage, setActivepage] = useState(initialPageNumber);

  const savedStatus = sessionStorage.getItem("softSchedulingFilter");

  const defaultStatusObj = status_list.find(item => item.key === 'this_month');
  const defaultStatusValue = defaultStatusObj ? defaultStatusObj.value : "";
  const initialStatus = savedStatus || defaultStatusValue;
  const [state, setState] = useState(initialStatus);

  const currentStatusObj = status_list.find((v) => v.value === state);
  const currentPeriodKey = currentStatusObj ? currentStatusObj.key : "this_month";
  const currentEmptyMessage = currentStatusObj ? currentStatusObj.messageEmpty : "No schedules";
  const [plantCapacity, setPlantCapacity] = useState({});
  const [openCreate, setCreate] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [action, setAction] = useState(true);
  const [showSnack, setSnack] = useState({});
  const [company, setCompany] = useState(getDefaultCompany(currentUserRole, "softSchedulingCompany"));
  const [isExportHidden, setIsExportHidden] = useState(false);

  useEffect(() => {
    const hidden = hideExportExcel(currentUserRole, roles);
    setIsExportHidden(hidden);
  }, [currentUserRole]);

  const ClearFormCancled = () => {
    setAction(false);
    setCreate(!openCreate);
  }

  // Uses the Derived Key for Plant Capacity
  function getTotalPlantCapacity(name){
    setCreate(!openCreate);
    getPlantCapacity(name, currentPeriodKey).then((response) => {
      setPlantCapacity(response)
    });
  }

  function exportLoadingText() {
    return loadingExcel ? commonStrings.Exporting : commonStrings.ExportToExcel;
  }

  const handleChange = (event) => {
    const value = event.target.value;
    setState(value);
    sessionStorage.setItem("softSchedulingFilter", value);
    // Reset pagination on filter change
    setActivepage(1);
    sessionStorage.setItem("selectedpagenumber", 1);
  };

  function loadingExcelVal(val) {
    setLoadingExcel(val);
  }

  const exportToExcel = () => {
    setLoadingExcel(true);
    if (exportExcelRef.current) {
      exportExcelRef.current.exportExcel();
    }
  };

  const handleCompanyChange = (event) => {
    setCompany(event.target.value);
    sessionStorage.setItem("softSchedulingCompany", event.target.value);
    setActivepage(1);
  };

  const payload1 = [
    {
      type: "label",
      value: softScheduleStrings.forms.sd,
      bold: true,
      sm: 6
    },
    {
      type: "label",
      value: plantCapacity.sd_capacity,
      sm: "6",
    },
    {
      type: "label",
      value: softScheduleStrings.forms.fd,
      bold: true,
      sm: "6",
    },
    {
      type: "label",
      value: plantCapacity.fd_capacity,
      sm: "6",
    },
    {
      type: "label",
      value: softScheduleStrings.forms.algo,
      bold: true,
      sm: "6",
    },
    {
      type: "label",
      value: plantCapacity.algo_capacity,
      sm: "6",
    },
  ];

  const createCalendar = () => (
    <Grid id="top-row" container>
      <Grid id="top-row" xs={12} container>
        <Template payload={payload1} />
      </Grid>
    </Grid>
  );

  const hasAccess = () => {
    const userRole = getCurrentUserDetails().role;
    return ([
      roles.managingDirector,
      roles.admin,
      roles.managerCommerical,
      roles.commercialExecutive,
      roles.commercialHod,
      roles.BusinessDevelopmentManager,
      roles.BDDomestic,
      roles.productionplanning,
      roles.BDManager,
      roles.Auditor,
      roles.BusinessDevelopmentHod,
      roles.Auditor,
      roles.BusinessDevelopmentExecutive,
      roles.managerPurchaseGC,
      roles.purchaseExecutiveGC].includes(userRole));
  };

  return (
    <Container className={globalClasses.sampleroot}>
      {showSnack.open && (
        <Snackbar {...showSnack} handleClose={() => setSnack({ open: false, message: '', severity: '' })} />
      )}
      <div className={classes.headerRow}>
        <div className={classes.headerLeft}>
          <div className={classes.filterItem}>
            <FilterSelect
              id="company-picklist"
              label={commonStrings.company}
              value={company}
              onChange={handleCompanyChange}
              options={getCompanyFilterList(currentUserRole)}
              disabled={isCompanyRestrictedRole(currentUserRole)}
            />
          </div>
          <div className={classes.filterItem}>
            <FilterSelect
              id="soft-schedule-picklist"
              label={commonStrings.pickList}
              value={state}
              onChange={handleChange}
              options={status_list}
            />
          </div>
          {hasAccess() &&
            factoryCodesArr.map((plant) => (
              <div className={classes.plantButtonWrap} key={plant.value}>
                <RoundButton
                  disabled={state !== "SS-ST-5" ? false : true}
                  onClick={() => getTotalPlantCapacity(plant.value)}
                  label={plant.label}
                />
              </div>
            ))
          }
        </div>
        <div className={classes.headerRight}>
          {isExportHidden && (
            totalRecords === 0 ? (
              <Tooltip title={commonStrings.noDataToExport} placement="top" arrow>
                <span>
                  <Fab
                    onClick={() => exportToExcel()}
                    label={""}
                    disabled
                    iconAddExcel="excel"
                  />
                </span>
              </Tooltip>
            ) : (
              <Tooltip title={commonStrings.exportToExcel} placement="top" arrow>
                <span>
                  <Fab
                    onClick={() => exportToExcel()}
                    label={""}
                    disabled={loadingExcel}
                    iconAddExcel="excel"
                  />
                </span>
              </Tooltip>
            )
          )}
          <SimpleModal
            open={openCreate}
            handleClose={() => setCreate(!openCreate)}
            header={softScheduleStrings.forms.totalCapacityConsumed}
            body={createCalendar}
            isNew={true}
            isFooter={true}
            footer={<Button label={commonStrings.cancel} onClick={ClearFormCancled} />}
            hasCancel={false}
            size="sm"
          />
        </div>
      </div>

      <SoftSchedulingList
        loadingExcelVal={loadingExcelVal}
        exportRef={exportExcelRef}
        activepage={activepage}
        setActivepage={setActivepage}
        totalrecord={totalRecords}
        updateTotalRecords={setTotalRecords}
        period={currentPeriodKey}
        messageEmpty={currentEmptyMessage}
        company={company}
      />
    </Container>
  );
};

export default SoftScheduling;
